import React from 'react';
import { PropTypes } from 'prop-types';
import Select from 'react-select';
import tailwindTheme from '../../../../../../config/tailwind.config';
import Option from './Option';
import SingleValue from './SingleValue';
import Icon from '../../Icons/Icon';
import DropdownIcon from '../SelectIconDropdown/DropdownIcon';
import { capitalizeFirstLetter } from '../../../utils/api';

const customStyles = (isRequired, value, fontSize, inputHeight, minHeight, inputPadding, paddingForInput, menuPosition, customWidth) => ({
  container: (provided) => ({
    ...provided,
    width: customWidth || '100%',
  }),
  control: (provided, state) => ({
    ...provided,
    width: '100%',
    height: inputHeight || 32,
    minHeight: minHeight || inputHeight || 32,
    borderRadius: 6,
    textAlign: 'left',
    fontFamily: 'DM Sans',
    borderColor: (() => {
      if (state.isFocused) {
        return tailwindTheme.theme.extend.colors['vamos-black'];
      } if (isRequired && (value === '' || value === null || value === undefined)) {
        return tailwindTheme.theme.extend.colors['vamos-red'];
      }
      return tailwindTheme.theme.extend.colors['vamos-blue-gray'];
    })(),
    borderWidth: 1,
    fontSize: fontSize || 16,
    fontWeight: 400,
    cursor: 'default',
    backgroundColor: state.isDisabled ? tailwindTheme.theme.extend.colors['vamos-light-gray'] : tailwindTheme.theme.extend.colors['vamos-white'],
    boxShadow: 'none',
    '> div': { padding: paddingForInput || '0 8px' }, // padding for the input
    '&:hover': {
      borderColor: 'border-vamos-blue-gray',
    },
    padding: inputPadding || 2,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: tailwindTheme.theme.extend.colors['vamos-gray'],
    fontSize: fontSize || 16,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: tailwindTheme.theme.extend.colors['vamos-black'],
    display: state.isDisabled ? 'none' : 'flex',
    pointerEvents: 'none',
    ariaHidden: true,
    padding: 2,
    transform: state.selectProps.menuIsOpen && 'rotate(180deg)',
  }),

  option: (provided, state) => {
    let color = tailwindTheme.theme.extend.colors['vamos-black'];

    if (state.value === 'InfoMessage') {
      color = tailwindTheme.theme.extend.colors['vamos-gray'];
    } else if (state.isSelected) {
      color = tailwindTheme.theme.extend.colors['vamos-black'];
    } else if (state.isDisabled) {
      color = tailwindTheme.theme.extend.colors['vamos-light-gray'];
    }

    const bg = state.isSelected
      ? tailwindTheme.theme.extend.colors['vamos-v-light-gray']
      : tailwindTheme.theme.extend.colors['vamos-white'];

    return {
      ...provided,
      color,
      padding: '5px',
      fontFamily: 'DM Sans',
      fontSize: fontSize || 16,
      fontWeight: 400,
      paddingLeft: '12px', // left margin for the option text in dropdown menu
      backgroundColor: bg,
      '&:hover': {
        backgroundColor:
        state.value === 'InfoMessage'
          ? tailwindTheme.theme.extend.colors['vamos-white']
          : tailwindTheme.theme.extend.colors['vamos-v-light-gray'],
      },
    };
  },
  singleValue: (provided) => ({
    ...provided,
    color: value === '' ? tailwindTheme.theme.extend.colors['vamos-gray'] : 'inherit',
  }),
  menu: (provided, state) => {
    const hasScroll = state.options && state.options.length > 9;
    return {
      ...provided,
      position: menuPosition,
      border: `1px solid ${tailwindTheme.theme.extend.colors['vamos-light-gray']}`,
      boxShadow: 'none',
      backgroundColor: tailwindTheme.theme.extend.colors['vamos-white'],
      paddingBottom: hasScroll ? 8 : 1,
      width: '100%',
      minWidth: '100%',
    };
  },
});

export const DEFAULT_SELECT_CLEAR_OPTION_LABEL = 'Select...';

const CustomSelect = ({
  options,
  setValue = () => { },
  label,
  value = undefined,
  isRequired = false,
  shouldShowClearOption = false,
  clearOptionLabel = DEFAULT_SELECT_CLEAR_OPTION_LABEL,
  asteriskWidth = 'w-2',
  errorMessage = '',
  labelFontSize = 'text-sm',
  defaultValue = undefined,
  fontSize = undefined, // font size in px, example fontSize={14}
  tooltip = undefined,
  spaceY = undefined,
  inputHeight = undefined, // height of input in px, example inputHeight={32}
  minHeight = undefined,
  placeholder = undefined,
  isLabelBeside = false,
  labelBesideLabelClasses = undefined,
  inputWidth = undefined, // useTailwindCSS classes for width, example inputWidth="w-1/2"
  errorMessageClass = undefined, // you can pass 'absolute' to make it absolute positioned
  customSingleValue = undefined, // allows to pass additional props to SingleValue component to customize how it looks
  name = undefined,
  isAlertIcon = false,
  errorMessageFontClasses = undefined,
  customDropdownOption = undefined, // allows to pass additional props to Option component to customize how it looks
  labelClassName = undefined,
  inputPadding = undefined,
  paddingForInput = undefined,
  onInputChange = undefined, // function to handle input change
  shouldNotCapitalize = false,
  menuPosition = 'absolute', // position of the dropdown menu, default is 'absolute'
  shouldFocus = false,
  isCursorShown = true, // to hide or show cursor
  customWidth = undefined, // to pass custom width style to the input container
  isDisabled = false,
  isOptionDisabled = () => false,
  dataTestId = undefined, // Wrapper-level test hook for reliable e2e selection.
}) => {
  // allows to pass additional props to SingleValue component
  const singleValueWithCustomProps = (props) => (
    <SingleValue {...props} customSingleValue={customSingleValue} />
  );

  // allows to pass additional props to Option component
  const optionWithCustomProps = (props) => (
    <Option {...props} customDropdownOption={customDropdownOption} dataTestId={props.data?.dataTestId} />
  );

  const normalizeOptions = (opts) => (opts || []).map((opt) => {
    if (!opt || opt.isClearOption || opt.value !== undefined) return opt;
    return { ...opt, value: opt.label };
  });

  const CLEAR_OPTION = {
    label: clearOptionLabel, value: '', isClearOption: true, dataTestId: `option-${clearOptionLabel.toLowerCase()}`,
  };

  const normalizedOptions = normalizeOptions(options);

  const computedOptions = shouldShowClearOption
    ? [CLEAR_OPTION, ...normalizedOptions]
    : normalizedOptions;

  const isEmptyValue = value === '' || value === null || value === undefined;
  const computedPlaceholder = placeholder ?? (shouldShowClearOption ? clearOptionLabel : undefined);

  // options is array of objects with label at least
  const defaultData = value
    ? computedOptions.filter((option) => {
      if (typeof value === 'string') return (option.label || '').toLowerCase() === value.toLowerCase();
      if (typeof value === 'object' && value !== null) return option.value === value.value;
      return false;
    })
    : computedOptions.filter((option) => {
      if (typeof defaultValue === 'string') return (option.label || '').toLowerCase() === defaultValue.toLowerCase();
      if (typeof defaultValue === 'object' && defaultValue !== null) return option.value === defaultValue.value;
      return false;
    });

  const selectedOption = (value === '' || value === null || value === undefined)
    ? null
    : defaultData[0] || null;
  return (
    <div
      data-testid={dataTestId}
      className={`${isLabelBeside
        ? 'flex items-center'
        : `${spaceY || 'space-y-1'} w-full`} 
    `}
    >
      {label && (
        <label className={`${isLabelBeside && 'font-bold'} ${labelFontSize || 'font-medium'} ${labelBesideLabelClasses || ''} inline cursor-default ${labelClassName} flex`} htmlFor={name}>
          {shouldNotCapitalize ? label : capitalizeFirstLetter(label)}
          {isRequired && <Icon name="required_field" className={`ml-1 mr-[2.5px] ${asteriskWidth}`} />}
          {tooltip && tooltip}
        </label>
      )}
      <div className={`${inputWidth || 'w-full'}`}>
        <Select
          key={customWidth}
          classNamePrefix="react-select"
          options={computedOptions}
          onChange={(e) => {
            if (e?.isClearOption) {
              setValue(null);
              return;
            }
            setValue(e);
          }}
          onInputChange={onInputChange ? (val, { action }) => {
            if (action === 'input-change') {
              const matchedOption = computedOptions.find(option => (option.label || '').toLowerCase() === val.toLowerCase());
              setValue({
                label: val,
                value: matchedOption ? matchedOption.value : val,
              });
            }
          } : undefined}
          value={isEmptyValue ? null : defaultData[0] || null}
          placeholder={computedPlaceholder}
          components={{
            Option: optionWithCustomProps,
            SingleValue: singleValueWithCustomProps,
            IndicatorSeparator: () => null,
            DropdownIndicator: DropdownIcon,
          }}
          styles={customStyles(isRequired, value, fontSize, inputHeight, minHeight, inputPadding, paddingForInput, menuPosition, customWidth)}
          defaultValue={defaultValue && defaultData[0]}
          inputId={name}
          name={name}
          autoFocus={shouldFocus}
          isSearchable={isCursorShown}
          isDisabled={isDisabled}
          isOptionDisabled={(option) => {
            const hasComparableValue = selectedOption
              && selectedOption.value !== undefined
              && option.value !== undefined;

            if (hasComparableValue && option.value === selectedOption.value) return true;

            return isOptionDisabled ? isOptionDisabled(option) : false;
          }}
        />
      </div>
      {/* render error message only if we have it */}
      {(isRequired && errorMessage && isEmptyValue)
      && (
      <h4 type="invalid" className={`flex ${errorMessageClass} ${errorMessageFontClasses || 'text-vamos-red text-sm font-normal'}`}>
        {isAlertIcon && <Icon name="alert" />}{errorMessage}
      </h4>
      )}
    </div>
  );
};

CustomSelect.propTypes = {
  options: PropTypes.array.isRequired,
  setValue: PropTypes.func,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  shouldShowClearOption: PropTypes.bool,
  clearOptionLabel: PropTypes.string,
  asteriskWidth: PropTypes.string,
  errorMessage: PropTypes.string,
  labelFontSize: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.object]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tooltip: PropTypes.node,
  spaceY: PropTypes.string,
  inputHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  isLabelBeside: PropTypes.bool,
  labelBesideLabelClasses: PropTypes.string,
  inputWidth: PropTypes.string,
  errorMessageClass: PropTypes.string,
  customSingleValue: PropTypes.func,
  name: PropTypes.string,
  isAlertIcon: PropTypes.bool,
  errorMessageFontClasses: PropTypes.string,
  customDropdownOption: PropTypes.func,
  labelClassName: PropTypes.string,
  inputPadding: PropTypes.string,
  paddingForInput: PropTypes.string,
  onInputChange: PropTypes.func,
  shouldNotCapitalize: PropTypes.bool,
  menuPosition: PropTypes.string,
  shouldFocus: PropTypes.bool,
  isCursorShown: PropTypes.bool,
  customWidth: PropTypes.string,
  isDisabled: PropTypes.bool,
  isOptionDisabled: PropTypes.func,
  dataTestId: PropTypes.string,
};

export default CustomSelect;
